import type { JSX } from 'react';
import { useI18n, type TranslationKeys } from '../i18n';

/**
 * P-4: the mode switch «Prep» / «Play» — the same two halves at the table and in Studio (mockup `.seg`).
 * The current half is pressed and filled with its surface's accent (Studio: amber, table: primary).
 * At the table the «Prep» half opens Studio; in Studio the «Play» half returns to the table.
 */
export interface ModeSwitchProps {
  readonly current: 'prep' | 'play';
  readonly accent: 'studio' | 'table';
  readonly onPrep: () => void;
  readonly onPlay: () => void;
  /** Stable hook for the «Prep» half at the table (older specs and the acceptance open Studio through it). */
  readonly prepTestId?: string;
  /** A class kept on the «Play» half (the Studio topbar: older specs find «back to the table» by it). */
  readonly playClassName?: string;
  /** The host's translate function (Studio passes its own); the app's i18n otherwise. */
  readonly t?: (key: keyof TranslationKeys) => string;
}

export function ModeSwitch({ current, accent, onPrep, onPlay, prepTestId, playClassName, t: hostT }: ModeSwitchProps): JSX.Element {
  const i18n = useI18n();
  const t = hostT ?? i18n.t;
  const on = accent === 'studio'
    ? 'bg-hl font-semibold text-hl-ink'
    : 'bg-primary font-semibold text-primary-foreground';
  const off = accent === 'studio'
    ? 'text-muted-foreground hover:text-fg'
    : 'text-muted-foreground hover:text-foreground';
  const frame = accent === 'studio' ? 'border-line' : 'border-border';
  const half = (id: 'prep' | 'play', label: string, onClick: () => void, testId?: string, extra?: string): JSX.Element => (
    <button
      type="button"
      className={`px-3 py-1 text-ui transition-colors ${current === id ? on : off} ${extra ?? ''}`}
      aria-pressed={current === id}
      data-testid={testId}
      onClick={onClick}
    >
      {label}
    </button>
  );
  return (
    <div
      role="group"
      aria-label={t('studio.mode.label')}
      className={`mode-switch inline-flex flex-none overflow-hidden rounded-md border ${frame}`}
    >
      {half('prep', t('studio.mode.prep'), onPrep, prepTestId)}
      {half('play', t('studio.mode.play'), onPlay, undefined, playClassName)}
    </div>
  );
}

export default ModeSwitch;
